import React, { ReactNode, useState } from 'react';

import { Tooltip } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useLocation } from 'react-router-dom';

import { LicenseType, ProductType } from '@/core/api/collections/users';
import { ProductRevokeConfirmationModal } from '@/core/users/UserPermissions/ProductRevokeConfirmationModal';
import { ICON_COLUMN_SIZE } from '@/core/users/UserPermissions/UserPermissionsContentWidth';
import routes from '@/routes';

const DEFAULT_ACTION_BUTTON_CLASSES = 'w-full px-6 py-4 rounded permission-group-toggle';
const DISABLED_ACTION_BUTTON_CLASSES = 'bg-gray-200 text-gray-600';
const ENABLED_ACTION_BUTTON_CLASSES = 'text-blue-100 bg-blue-900 font-medium';

type Props = {
  product: ProductType;
  description: string;
  renderPill?: () => ReactNode;
  renderLogo: () => ReactNode;
  disabledMessage?: string;
  hasAccess: boolean;
  onAccessToggled: (hasAccess: boolean) => void;
  children: ReactNode;
  licenseType?: LicenseType;
}
export function ProductPermissionGroup ({ product, renderPill, description, renderLogo, disabledMessage, hasAccess, onAccessToggled, children, licenseType }: Props) {
  const { pathname } = useLocation();
  const [isExpanded, setIsExpanded] = useState(hasAccess);
  const [showRevokeConfirmation, setShowRevokeConfirmation] = useState(false);
  const ExpandIcon = isExpanded ? ExpandLess : ExpandMore;

  const handleAccessToggled = () => {
    // we don't need to display the revoke confirmation on the invite page
    if (pathname.indexOf(routes.users.invite) === -1 && hasAccess) {
      setShowRevokeConfirmation(true);
    } else {
      setIsExpanded(!isExpanded);
      onAccessToggled(!hasAccess);
    }
  };

  const handleRevokeConfirmation = () => {
    setIsExpanded(false);
    setShowRevokeConfirmation(false);
    onAccessToggled(false);
  };

  return (
    <>
      {hasAccess && (
        <button
          className="flex items-center justify-center"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ height: ICON_COLUMN_SIZE }}
        >
          <ExpandIcon />
        </button>
      )}
      <div className="col-start-2" style={{ height: ICON_COLUMN_SIZE }}>
        { renderLogo() }
      </div>
      <div className="col-span-3">
        <h2 className="text-xl font-medium text-gray-900">
          <div className="flex items-center">
            {product === 'discovery' && 'UserVoice'}
            {product === 'validation' && 'Validation'}
            {renderPill && (
              <div>
                { renderPill() }
              </div>
            )}
          </div>
        </h2>
        <p className="mt-1 font-light">{ description }</p>
      </div>
      <div className="flex">
        <Tooltip title={disabledMessage || ''}>
          <button
            className={`${disabledMessage ? DISABLED_ACTION_BUTTON_CLASSES : ENABLED_ACTION_BUTTON_CLASSES} ${DEFAULT_ACTION_BUTTON_CLASSES}`}
            onClick={disabledMessage ? undefined : handleAccessToggled}
          >
            {hasAccess ? 'Remove Access' : 'Enable Access'}
          </button>
        </Tooltip>
      </div>
      { isExpanded && children }
      <ProductRevokeConfirmationModal
        product={product}
        onClose={() => setShowRevokeConfirmation(false)}
        onSubmit={handleRevokeConfirmation}
        open={showRevokeConfirmation}
        licenseType={licenseType}
      />
    </>
  );
}
